import get from 'lodash/fp/get';
import isString from 'lodash/fp/isString';
import values from 'lodash/fp/values';
import isObject from 'lodash/fp/isObject';
import { captureExceptionWithRecording } from '@whitelabel/helpers/utils';
import commonMsg from '@whitelabel/helpers/messages/commonMsg';
import { isJSON } from '@whitelabel/helpers/objects';
import { getSelectedAPIHost } from '@whitelabel/xcover-shared/helpers/multiRegion';
import { logErrorToDatadog } from './datadogErrorLogging';
import { CP_ROOT } from './constants';
import { ICPError } from './types';
import { appIntl } from './intlSingletonService';

function getAPIHost(id?: string, version: 'v1' | 'v2' = 'v1', CPBackendPercent = 0, customerID?: string) {
  const addVersion = (url: string) => `${url}/${version}`;

  const useXCPBackend = CPBackendPercent && Math.random() <= CPBackendPercent;

  const apSouth1APIHost = addVersion(
    useXCPBackend
      ? process.env.NEXT_PUBLIC_CP_AP_SOUTH_1_API_HOST! || process.env.REACT_APP_CP_AP_SOUTH_1_API_HOST!
      : process.env.NEXT_PUBLIC_AP_SOUTH_1_API_HOST!,
  );
  const usEast1APIHost = addVersion(
    useXCPBackend
      ? process.env.NEXT_PUBLIC_CP_US_EAST_1_API_HOST! || process.env.REACT_APP_CP_US_EAST_1_API_HOST!
      : process.env.NEXT_PUBLIC_US_EAST_1_API_HOST!,
  );
  const euCentral1APIHost = addVersion(
    useXCPBackend
      ? process.env.NEXT_PUBLIC_CP_API_HOST! || process.env.REACT_APP_CP_API_HOST!
      : process.env.NEXT_PUBLIC_API_HOST || '/api',
  );

  return getSelectedAPIHost(euCentral1APIHost, usEast1APIHost, apSouth1APIHost, id, customerID);
}

/**
 * API Error Response might not have a message field.
 */
type IRawResponse = string | (Omit<ICPError, 'message'> & { message?: string });

/**
 * Get the API Host for claims portal (XCover.com API for XC and claims-portal-backend for RC)
 */
export const getCPAPIHost = (
  {
    id,
    version,
    CPBackendPercent,
    customerID,
  }: {
    id?: string;
    version?: 'v1' | 'v2';
    CPBackendPercent?: number;
    customerID?: string;
  } = { id: undefined, version: 'v1', CPBackendPercent: 0, customerID: undefined },
) => (CP_ROOT === 'xcover' ? getAPIHost(id, version, CPBackendPercent, customerID) : process.env.VITE_API_HOST);

const processResponse = (rawResponse: IRawResponse) => {
  const isUnParsedResponse = isString(rawResponse) && isJSON(rawResponse);
  return (isUnParsedResponse ? JSON.parse(rawResponse as string) : rawResponse) as IRawResponse;
};

const getHTMLError = (html?: string) => {
  if (!html) return undefined;
  const doc = new DOMParser().parseFromString(html, 'text/xml');
  const title = doc.getElementsByTagName('title')?.[0]?.innerHTML;
  return title ? `HTML Error: ${title}` : undefined;
};

const getErrorMessage = (response: IRawResponse) => {
  let appErrorMessage;
  let sentryErrorMessage;
  const commonErrorMsg = appIntl()?.formatMessage(commonMsg.error);

  if (isString(response)) {
    appErrorMessage = commonErrorMsg;
    sentryErrorMessage = getHTMLError(response) || response;
  } else {
    const msgField = get('message', response);
    const msgInErrorsField = values<string[] | undefined>(get('errors', response))?.[0]?.toString();
    const msgInExtraField = values<string | undefined>(get('extra', response))?.[0];
    const type = get('type', response);
    const code = get('code', response);
    let apiErrorMessage;
    apiErrorMessage = msgInErrorsField || msgInExtraField || msgField;
    if (isObject(apiErrorMessage)) {
      apiErrorMessage = appIntl()?.formatMessage(commonMsg.error);
    }
    const htmlError = getHTMLError(apiErrorMessage);

    appErrorMessage = htmlError ? commonErrorMsg : apiErrorMessage || commonErrorMsg;
    sentryErrorMessage = `${type || code}: ${htmlError || apiErrorMessage || 'No error message'}`;
  }

  return { appErrorMessage, sentryErrorMessage };
};

interface ICreateCPErrorArgs {
  /**
   * most of the time, the status is HTTP status code.
   * but sometimes, it can be a string,
   * e.g. "PARSING_ERROR" when response is not returned correctly.
   */
  status: number | string;
  /**
   * error response currently is handled with the below content types:
   * 1. application/json
   * 2. text/html
   * 3. plain text
   */
  response: IRawResponse;
  responseHeaders?: Headers;
  /**
   * url might be empty when the queryFn is defined in the endpoint.
   */
  url?: string;
  endpointName: string;
}

export const createCPError = ({
  status,
  response: rawResponse,
  responseHeaders,
  url,
  endpointName,
}: ICreateCPErrorArgs): ICPError => {
  const response = processResponse(rawResponse);
  const { appErrorMessage, sentryErrorMessage } = getErrorMessage(response);

  const appError: ICPError = new Error(appErrorMessage, { cause: response });
  const sentryError: ICPError = new Error(sentryErrorMessage, { cause: response });

  const requestId = responseHeaders?.get('X-Request-Id');

  [appError, sentryError].forEach((error) => {
    error.isApiError = true;
    error.status = status;
    error.requestId = requestId;
    error.name = `${endpointName} - ${status}`;
    if (!isString(response)) {
      error.type = get('type', response);
      error.code = get('code', response);
      error.isJSON = isJSON(rawResponse);
      error.errors = get('errors', response);
      error.extra = get('extra', response);
    }
  });

  const pathname = url ? new URL(url).pathname : null;

  logErrorToDatadog(sentryError, {
    source: 'api',
    endpoint_name: endpointName,
    status_code: status,
    request_id: requestId,
    type: sentryError.type,
    code: sentryError.code,
    errors: sentryError.errors,
    extra: sentryError.extra,
  });

  captureExceptionWithRecording(sentryError, { url: pathname, errors: sentryError.errors, extra: sentryError.extra });

  return appError;
};
